import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import useAdminStatus from "./Hooks/useAdminStatus"; // Hook to get admin status

// PrivateRoute: Protects routes and ensures only authenticated users can access them
function PrivateRoute({ children }) {
  const [loading, setLoading] = React.useState(true);
  const [user, setUser] = React.useState(null);
  const auth = getAuth();

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
      setLoading(false); // Stop loading when user state is resolved
    });

    return () => unsubscribe();
  }, [auth]);

  if (loading) {
    return <div>Loading...</div>; // Placeholder for loading state
  }

  if (!user) {
    return <Navigate to="/" />; // Redirect to home if not authenticated
  }

  // Pass the user state down to child components
  return children ? children : <Outlet />;
}

// AdminRoute: Protects admin-only routes and checks both authentication and admin status
function AdminRoute({ children }) {
  const { isAdmin, loading: adminLoading } = useAdminStatus();
  const auth = getAuth();
  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
    });

    return () => unsubscribe();
  }, [auth]);

  if (adminLoading || !user) {
    return <div>Checking permissions...</div>; // Show loading while checking user and admin status
  }

  if (!isAdmin) {
    return <Navigate to="/" />; // Redirect if user is not an admin
  }

  return children ? children : <Outlet />;
}

export { PrivateRoute, AdminRoute };
